/**
 * App 配置上下文：
 * - 提供 menuConfig（当前仅包含 mode: 'vertical'），可按需扩展。
 * - 通过 AppConfigContextProvider 包裹页面/路由，子组件用 useAppConfigContext 读写。
 * - 缺少 Provider 时会抛错，便于在开发阶段及时发现忘记包裹的问题。
 */

import { useModel } from '@umijs/max';
import { message } from 'antd';
import React, {
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface AppConfigProviderType {
  children: ReactNode;
}

interface AppConfigContextType {
  menuConfig: any;
  setMenuConfig: (v: any) => void;
  showSuccess: (v: any) => void;
  showError: (v: any) => void;
  showWarning: (v: any) => void;
  showInfo: (v: any) => void;
  userUuid: string;
}

// 全局共享的配置上下文，默认值用于兜底初始化
const AppConfigContext = React.createContext<AppConfigContextType>({
  menuConfig: {},
  setMenuConfig: () => {},
  showSuccess: () => {},
  showError: () => {},
  showWarning: () => {},
  showInfo: () => {},
  userUuid: '',
});

const AppConfigContextProvider = ({ children }: AppConfigProviderType) => {
  // 从 Umi 的 initialState 中获取用户信息
  const { initialState } = useModel('@@initialState');
  const userUuid = initialState?.currentUser?.uuid || '';

  // 仅维护 menu 模式，后续可按需扩展（如主题、布局等）
  const [menuConfig, setMenuConfig] = useState({
    mode: 'vertical',
  });

  const [messageApi, contextHolder] = message.useMessage();
  const showMessage = useCallback(
    (type: 'success' | 'error' | 'warning' | 'info', content: string) => {
      messageApi[type](content);
    },
    [messageApi],
  );

  const showSuccess = useCallback(
    (content: string) => showMessage('success', content),
    [showMessage],
  );

  const showError = useCallback(
    (content: string) => showMessage('error', content),
    [showMessage],
  );

  const showWarning = useCallback(
    (content: string) => showMessage('warning', content),
    [showMessage],
  );

  const showInfo = useCallback(
    (content: string) => showMessage('info', content),
    [showMessage],
  );

  const value = useMemo<AppConfigContextType>(
    () => ({
      menuConfig,
      userUuid,
      setMenuConfig,
      showSuccess,
      showError,
      showWarning,
      showInfo,
    }),
    [
      menuConfig,
      userUuid,
      setMenuConfig,
      showSuccess,
      showError,
      showWarning,
      showInfo,
    ],
  );

  // 提供上下文给子树
  return (
    <AppConfigContext.Provider value={value}>
      {children}
      {contextHolder}
    </AppConfigContext.Provider>
  );
};

const useAppConfigContext = () => {
  const context = useContext(AppConfigContext);
  if (context === undefined) {
    throw new Error(
      'useAppConfigContext must be used within an AppConfigContextProvider',
    );
  }
  return context;
};

export { useAppConfigContext, AppConfigContextProvider };
